import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, PointerEvent as ReactPointerEvent } from "react";
import { errorText } from "../../api/client";
import { useStore } from "../store";
import { kidStyle } from "../kid-style";
import { tapFeedback } from "../feedback";
import { KidAvatar } from "./KidAvatar";
import type { Kid } from "../../shared/types";

/** The saved photo: a square this many pixels across, enough for the biggest avatar on a 2x screen. */
const PHOTO_PX = 256;
const JPEG_QUALITY = 0.85;
/** The preview's own pixels, so it stays sharp on a 3x screen. */
const PREVIEW_PX = 512;
/** A big camera photo is shrunk to this on its short side once, so dragging redraws quickly. */
const WORKING_PX = 1024;
const UNSUPPORTED = "That photo type isn't supported, try a screenshot or another photo.";

/** A decoded photo, shrunk to WORKING_PX at most on its short side. */
type Picture = { canvas: HTMLCanvasElement; side: number };

/**
 * A sheet to set or remove a kid's photo. The phone offers its camera or its library; the photo
 * is cropped to a centred square the grown-up can drag into place, shrunk to 256px and sent as a
 * JPEG of 15-30 KB, so the upload is quick and the household stores almost nothing.
 */
export function PhotoPicker({ kid, onClose }: { kid: Kid; onClose: () => void }) {
  const { setKidPhoto, removeKidPhoto } = useStore();
  const [picture, setPicture] = useState<Picture | null>(null);
  // The crop square's top-left corner, in the picture's pixels.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const preview = useRef<HTMLCanvasElement>(null);
  const drag = useRef<{ pointer: number; x: number; y: number; from: { x: number; y: number } } | null>(null);
  const firstButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    firstButton.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  useEffect(() => {
    const ctx = preview.current?.getContext("2d");
    if (picture && ctx) drawCrop(ctx, picture, offset, PREVIEW_PX);
  }, [picture, offset]);

  async function choose(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Cleared so choosing the same photo again still counts as a change.
    e.target.value = "";
    if (!file) return;
    setErr(null);
    try {
      const pic = await decode(file);
      const short = Math.min(pic.canvas.width, pic.canvas.height);
      setPicture({ ...pic, side: short });
      setOffset({ x: (pic.canvas.width - short) / 2, y: (pic.canvas.height - short) / 2 });
    } catch {
      setErr(UNSUPPORTED);
    }
  }

  function startDrag(e: ReactPointerEvent<HTMLCanvasElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { pointer: e.pointerId, x: e.clientX, y: e.clientY, from: offset };
  }

  function moveDrag(e: ReactPointerEvent<HTMLCanvasElement>) {
    const d = drag.current;
    if (!picture || !d || d.pointer !== e.pointerId) return;
    // Screen pixels to picture pixels: the preview shows `side` picture pixels across its width.
    const scale = picture.side / e.currentTarget.getBoundingClientRect().width;
    const clamp = (v: number, max: number) => Math.min(Math.max(v, 0), max);
    // Dragging the photo right shows more of its left, so the crop moves the other way.
    setOffset({
      x: clamp(d.from.x - (e.clientX - d.x) * scale, picture.canvas.width - picture.side),
      y: clamp(d.from.y - (e.clientY - d.y) * scale, picture.canvas.height - picture.side),
    });
  }

  function endDrag() {
    drag.current = null;
  }

  async function save() {
    if (!picture) return;
    setBusy(true);
    setErr(null);
    try {
      const out = document.createElement("canvas");
      out.width = PHOTO_PX;
      out.height = PHOTO_PX;
      const ctx = out.getContext("2d");
      if (!ctx) throw new Error("This browser can't resize photos.");
      drawCrop(ctx, picture, offset, PHOTO_PX);
      const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
      if (!blob) throw new Error("This browser can't resize photos.");
      await setKidPhoto(kid.id, blob);
      tapFeedback(25);
      onClose();
    } catch (e) {
      setErr(errorText(e, "Couldn't save the photo."));
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setErr(null);
    try {
      await removeKidPhoto(kid.id);
      tapFeedback(25);
      onClose();
    } catch (e) {
      setErr(errorText(e, "Couldn't remove the photo."));
      setBusy(false);
    }
  }

  return (
    <div className="sheet" style={kidStyle(kid.colour)}>
      <div className="sheet__backdrop" onClick={() => !busy && onClose()} />
      <section className="sheet__panel photo-picker" role="dialog" aria-modal="true" aria-labelledby="photo-picker-title">
        <div className="sheet__head">
          <h2 className="sheet__title" id="photo-picker-title">
            {`Photo of ${kid.name}`}
          </h2>
          <button ref={firstButton} type="button" className="button button--small" disabled={busy} onClick={onClose}>
            Cancel
          </button>
        </div>

        {picture ? (
          <>
            <div className="photo-crop">
              <canvas
                ref={preview}
                className="photo-crop__canvas"
                width={PREVIEW_PX}
                height={PREVIEW_PX}
                aria-label={`Drag to move the photo of ${kid.name}`}
                onPointerDown={startDrag}
                onPointerMove={moveDrag}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              />
              <span className="photo-crop__ring" aria-hidden="true" />
            </div>
            <p className="hint photo-picker__hint">Drag to move the photo. The circle is what shows.</p>
          </>
        ) : (
          <div className="photo-picker__current">
            <KidAvatar kid={kid} size="xl" />
          </div>
        )}

        {err && <p className="error">{err}</p>}

        <div className="photo-picker__actions">
          {picture && (
            <button className="button button--primary" type="button" disabled={busy} onClick={() => void save()}>
              {busy ? "Saving…" : "Save photo"}
            </button>
          )}
          {/* Two inputs: `capture` sends a phone straight to its front camera, and without it the
              phone offers its library (iOS also offers the camera there). A computer has no
              camera picker, so CSS hides the first one where the pointer is a mouse. */}
          <label className={`button photo-picker__file photo-picker__camera${busy ? " photo-picker__file--busy" : ""}`}>
            <input
              className="visually-hidden"
              type="file"
              accept="image/*"
              capture="user"
              disabled={busy}
              onChange={(e) => void choose(e)}
            />
            Take a photo
          </label>
          <label className={`button photo-picker__file${busy ? " photo-picker__file--busy" : ""}`}>
            <input
              className="visually-hidden"
              type="file"
              accept="image/*"
              disabled={busy}
              onChange={(e) => void choose(e)}
            />
            {picture ? "Choose another" : "Choose a photo"}
          </label>
          {!picture && kid.photoUpdatedAt !== null && (
            <button className="button button--ghost" type="button" disabled={busy} onClick={() => void remove()}>
              {busy ? "Removing…" : "Remove photo"}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

/** Draws the crop square of `picture` at `offset` to fill a `px` square canvas. */
function drawCrop(ctx: CanvasRenderingContext2D, picture: Picture, offset: { x: number; y: number }, px: number) {
  // White under a transparent PNG, which would otherwise turn black as a JPEG.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, px, px);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(picture.canvas, offset.x, offset.y, picture.side, picture.side, 0, 0, px, px);
}

/**
 * The file as a canvas no more than WORKING_PX on its short side. The browser decodes it, so
 * whatever the phone's own browser can show works, iPhone HEIC photos in Safari included, and
 * the photo comes out the right way up. createImageBitmap first; an <img> from a data: URL
 * (allowed by the CSP, unlike blob:) for a browser whose createImageBitmap refuses the file.
 */
async function decode(file: File): Promise<{ canvas: HTMLCanvasElement }> {
  let image: ImageBitmap | HTMLImageElement;
  try {
    image = await createImageBitmap(file);
  } catch {
    const url = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error ?? new Error("unreadable"));
      reader.readAsDataURL(file);
    });
    const img = new Image();
    img.src = url;
    await img.decode();
    image = img;
  }
  const w = image.width;
  const h = image.height;
  if (!w || !h) throw new Error("empty image");
  const scale = Math.min(1, WORKING_PX / Math.min(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no canvas");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  if ("close" in image) image.close();
  return { canvas };
}
